import { useEffect, useRef, useState } from "react";
import { UserRound } from "lucide-react";
import Logo from "./Logo";
import AccountMenu from "./AccountMenu";

const links = [
  { href: "#fonctionnement", label: "Fonctionnement" },
  { href: "#avantages", label: "Avantages" },
  { href: "#tarifs", label: "Tarifs" },
  { href: "#faq", label: "FAQ" },
];

const Header = ({
  onTryDemo,
  onStartTrial,
  onLogin,
  session,
  onLogout,
  onOpenSimulator,
  onOpenSettings,
  onOpenAdmin,
  onChangePassword,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);
  const menuOpen = openMenu === "account";
  const toggleMenu = (name) => setOpenMenu((current) => (current === name ? null : name));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!openMenu) return undefined;
    const onPointer = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpenMenu(null);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenu]);

  const who = session
    ? session.role === "student"
      ? session.studentName
      : session.role === "parent"
        ? session.parentName
        : session.name
    : "";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-gray-200/80"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8" data-testid="primary-nav">
          {(session ? [] : links).map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-[#4a5f53] hover:text-[#122118] transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {!session && (
            <>
              <button
                onClick={onTryDemo}
                data-testid="header-demo-button"
                className="hidden sm:inline-flex text-sm font-semibold text-[#122118] hover:text-[#189346] transition-colors"
              >
                Voir la démo
              </button>
              <button
                type="button"
                onClick={onStartTrial}
                data-testid="header-cta-button"
                className="inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold transition-all"
                style={{
                  background: "var(--sp-primary)",
                  color: "var(--sp-text-on-primary)",
                }}
              >
                Essai gratuit
              </button>
            </>
          )}
          {session ? (
            <AccountMenu
              session={session}
              who={who}
              onLogout={onLogout}
              onOpenSettings={onOpenSettings}
              onOpenSimulator={onOpenSimulator}
              onOpenAdmin={onOpenAdmin}
              onChangePassword={onChangePassword}
            />
          ) : (
          <div className="flex items-center gap-1" ref={menuRef}>
          <div className="relative">
            <button
              type="button"
              data-testid="header-account"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label="Compte"
              onClick={() => toggleMenu("account")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-[#122118] hover:border-[#25D366]"
            >
              <UserRound className="h-5 w-5" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                data-testid="header-account-menu"
                className="absolute right-0 top-full z-20 mt-2 w-52 overflow-hidden rounded-2xl border border-gray-100 bg-white py-1 shadow-xl"
              >
                <button
                  type="button"
                  role="menuitem"
                  data-testid="header-signup"
                  onClick={() => {
                    setOpenMenu(null);
                    onStartTrial();
                  }}
                  className="block w-full px-4 py-2.5 text-left text-sm font-semibold text-[#122118] hover:bg-[#F3FCF7]"
                >
                  S'inscrire
                </button>
                <button
                  type="button"
                  role="menuitem"
                  data-testid="header-login"
                  onClick={() => {
                    setOpenMenu(null);
                    onLogin();
                  }}
                  className="block w-full px-4 py-2.5 text-left text-sm font-semibold text-[#122118] hover:bg-[#F3FCF7]"
                >
                  Se connecter
                </button>
              </div>
            )}
          </div>
          </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
